/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ArrowIcon, Button } from "@cmsgov/ds-medicare-gov";
import FocusTrap from "focus-trap-react";
import React, {
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { HashLink } from "react-router-hash-link";
import { DrugCostInfo } from "../../../@types";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
} from "../../../app/contexts/Analytics/types";
import { AppStore } from "../../../app/store";
import { useBelowLargeBreakpoint, useTranslate } from "../../../helpers";
import { StickyNavE2EClassName } from "../../../helpers/CONSTANTS";

interface NavItem {
  label: string;
  section: string;
  ref: RefObject<HTMLElement>;
}

interface StickyNavProps extends RouteComponentProps {
  items: NavItem[];
  drugCostInfo: DrugCostInfo;
}

const StickyNav: React.FunctionComponent<StickyNavProps> = ({
  items,
  history,
  location,
  drugCostInfo,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppStore.AppContext);
  const [activeSection, setActiveSection] = useState(
    items.find(item => item.section === location.hash.slice(1))
  );
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileSize, setIsMobileSize] = useState(false);
  const navRef = useRef<HTMLUListElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const highlighterRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef(location);
  const observer = new IntersectionObserver(
    ([entry]: IntersectionObserverEntry[]): void => {
      if (entry.isIntersecting) {
        const activeSection = items.find(
          item => item.section === entry.target.id
        );
        setActiveSection(activeSection);
        if (window.scrollY !== 0) {
          locationRef.current.hash = entry.target.id;
          history.replace(locationRef.current);
        }
      }
    },
    { threshold: 0.2 } // threshold might need to change as we flesh out the sections
  );

  useEffect(() => {
    if (activeSection) {
      const activeItemLink = document.querySelector(
        `#${activeSection.section}-link`
      );

      if (activeItemLink) {
        const { x } = activeItemLink.getBoundingClientRect();

        if (highlighterRef.current) {
          highlighterRef.current.style.left = `${x}px`;
          highlighterRef.current.style.width = `${activeItemLink.clientWidth}px`;
        }
      }
    }
  }, [activeSection]);

  const scrollToSection = (el: HTMLElement): void => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yMobileToggleOffset = toggleRef.current
      ? toggleRef.current.offsetHeight
      : 0;
    const yNavOffset = navRef.current ? navRef.current.offsetHeight : 0;
    el.focus();
    window.scrollTo({
      top:
        yCoordinate - (yMobileToggleOffset ? yMobileToggleOffset : yNavOffset),
    });
  };

  const toggleOpen = (): void => {
    setIsMobileOpen(!isMobileOpen);
  };

  useBelowLargeBreakpoint(matches => {
    setIsMobileSize(matches);
  });

  useEffect(() => {
    const currentSection = items.find(
      item => item.section === activeSection?.section
    );
    const currentSectionRef = currentSection?.ref.current;
    const appendedHash = window.location.hash.match(/[^/]#(?<hash>.*)$/)?.groups
      ?.hash;
    if (currentSectionRef && appendedHash === currentSectionRef.id) {
      scrollToSection(currentSectionRef);
    }
    items.forEach(item => {
      if (item.ref?.current) {
        observer.observe(item.ref.current);
      }
    });
    return (): void => {
      observer.disconnect();
    };
  }, [drugCostInfo]);

  // the items change if the language changes so if that happens, we want to
  // update the activeSection so it has the correct translation
  useEffect(() => {
    const activeItem = items.find(
      item => item.section === activeSection?.section
    );
    setActiveSection(activeItem);
  }, [items]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.classList.add("no-scroll-on-mobile");
    } else {
      document.body.classList.remove("no-scroll-on-mobile");
    }
  }, [isMobileOpen]);

  // if the location changes (for example when the language switches)
  // update the locationRef so the intersection observer callback has the
  // latest value
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  return (
    <>
      <div
        className={`StickyNav__background${isMobileOpen ? "" : " closed"}`}
        onClick={(): void => setIsMobileOpen(false)}
      />
      <FocusTrap active={isMobileSize && isMobileOpen}>
        <nav
          className={`StickyNav ${StickyNavE2EClassName}`}
          aria-label={t("sticky_nav.page_section")}
        >
          <div ref={toggleRef}>
            <Button
              className="StickyNav__mobile_toggle"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.DEFAULT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: activeSection?.label || "",
                    },
                  },
                });
                toggleOpen();
              }}
              aria-expanded={isMobileOpen}
              aria-controls="sticky-nav-items"
            >
              {activeSection?.label}
              <ArrowIcon direction={isMobileOpen ? "up" : "down"} />
            </Button>
          </div>
          <ul
            id="sticky-nav-items"
            ref={navRef}
            className={`StickyNav__list${
              !isMobileOpen ? " mobile_closed" : ""
            }`}
            role="list"
          >
            {items.map(item => {
              return (
                <li key={item.section} role="listitem">
                  <HashLink
                    id={`${item.section}-link`}
                    to={`#${item.section}`}
                    className={
                      item.section === activeSection?.section ? "active" : ""
                    }
                    onClick={() => {
                      setActiveSection(item);
                      setIsMobileOpen(false);
                      dispatch({
                        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                        settings: {
                          event_action: "Plan Details Navigation Tab Clicked",
                          event_label: item.label,
                        },
                      });
                      dispatch({
                        type: AnalyticsActionType.SEND_GA4_EVENT,
                        settings: {
                          event_name: Ga4Event.PLAN_DETAILS_NAVIGATION_CLICKED,
                          click_text: item.label,
                        },
                      });
                    }}
                    scroll={scrollToSection}
                    tabIndex={isMobileSize && !isMobileOpen ? -1 : 0}
                  >
                    {item.label}
                  </HashLink>
                </li>
              );
            })}
          </ul>
          <div ref={highlighterRef} className="StickyNav__highlighter"></div>
        </nav>
      </FocusTrap>
    </>
  );
};

export default withRouter(StickyNav);
